import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import {
  defaultHistoryFilters,
  filterActivityLog,
  formatLogTime,
  getActivityLog,
  logTypeLabel,
  splitChanges,
  toActivityLogExportRows,
} from "../../../helpers/activityLog";
import { downloadExcel, safeFileName } from "../../../helpers/excelExport";
import ActivityHistoryToolbar from "./ActivityHistoryToolbar";

/**
 * History (UserActivityLog) as a timeline: what changed, who and when, newest first, with a search
 * (text + log date range, the last 30 days at first; Clear filters shows everything) and Excel export of the entries shown.
 * - one record: logType (LOG_TYPES value) + columnValue1 / columnValue2;
 * - a user's activity on every screen: userName; each entry then also shows its screen and record.
 * exportName (optional) replaces the record part of the export file name.
 */
export default function ActivityHistory({
  logType,
  columnValue1,
  columnValue2 = "",
  userName,
  emptyText = "No history yet.",
  reloadKey,
  exportName,
}) {
  const [state, setState] = useState({ loading: true, rows: [], error: "" });
  const [filters, setFilters] = useState(defaultHistoryFilters); // opens on the last 30 days

  useEffect(() => {
    let active = true;
    setState({ loading: true, rows: [], error: "" });
    getActivityLog(userName ? { logType, columnValue1, columnValue2, userName } : { logType, columnValue1, columnValue2 })
      .then((rows) => active && setState({ loading: false, rows, error: "" }))
      .catch((err) => active && setState({ loading: false, rows: [], error: err?.message || "Unable to load the history." }));
    return () => {
      active = false;
    };
  }, [logType, columnValue1, columnValue2, userName, reloadKey]);

  const shown = useMemo(() => filterActivityLog(state.rows, filters), [state.rows, filters]);

  const exportShown = () => {
    const subject =
      exportName || (userName ? userName : [logTypeLabel(logType), columnValue1, columnValue2].filter(Boolean).join("_"));
    downloadExcel(
      toActivityLogExportRows(shown, { withRecord: Boolean(userName) }),
      `History_${safeFileName(subject)}_${moment().format("YYYYMMDD-HHmm")}.xlsx`,
      "History",
    );
  };

  if (state.loading) {
    return (
      <p className="mst-history__state">
        <i className="fas fa-spinner fa-spin" /> Loading history...
      </p>
    );
  }
  if (state.error) {
    return (
      <p className="mst-history__state text-danger">
        <i className="fas fa-exclamation-triangle" /> {state.error}
      </p>
    );
  }
  if (!state.rows.length) {
    return (
      <p className="mst-history__state">
        <i className="fas fa-history" /> {emptyText}
      </p>
    );
  }
  return (
    <>
      <ActivityHistoryToolbar
        filters={filters}
        onChange={setFilters}
        shown={shown.length}
        total={state.rows.length}
        onExport={exportShown}
      />
      {!shown.length && (
        <p className="mst-history__state">
          <i className="fas fa-search" />{" "}
          {filters.text ? "No history matches the search." : "No history on the selected dates."} Use Clear filters to
          see all {state.rows.length} {state.rows.length === 1 ? "entry" : "entries"}.
        </p>
      )}
      <ol className="mst-history">
        {shown.map((row) => {
          const changes = splitChanges(row.description);
          return (
            <li key={row.id} className="mst-history__item">
              {userName && (
                <div className="mst-history__record">
                  <span className="mst-tag">{logTypeLabel(row.logType)}</span>
                  {row.columnValue1 && <span className="mst-history__key">{row.columnValue1}</span>}
                  {row.columnValue2 && <span className="mst-history__key">{row.columnValue2}</span>}
                </div>
              )}
              <div className="mst-history__meta">
                <span className="mst-history__user">
                  <i className="fas fa-user" aria-hidden="true" /> {row.userName || "Unknown user"}
                </span>
                <time className="mst-history__time">{formatLogTime(row.createdTime)}</time>
              </div>
              {changes.length > 1 ? (
                <ul className="mst-history__changes">
                  {changes.map((change, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <li key={i}>{change}</li>
                  ))}
                </ul>
              ) : (
                <p className="mst-history__text">{changes[0] || row.description}</p>
              )}
            </li>
          );
        })}
      </ol>
    </>
  );
}
